"use client";

import { useEffect, useRef } from "react";
import { ToolNavigationLink } from "@/components/shell/ToolNavigationLink";
import { Clock } from "lucide-react";
import { phCapture } from "@/utils/analytics";
import { EmptyState } from "@/components/ui/empty-state";
import { PeakTimeChart } from "@/components/tools/peak-time-chart";
import { LANDING_STATS } from "@/lib/landing-constants";

interface FirstTimeEmptyStateProps {
  hasFilters?: boolean;
  page: "optimizer" | "props";
  title?: string;
  description?: string | null;
  totalBeforeFilters?: number;
  onResetFilters?: () => void;
  onRetry?: () => void;
  trackingVariant?: "first_time" | "generic";
  showPeakTimeChart?: boolean;
}

export function FirstTimeEmptyState({
  hasFilters = false,
  page,
  title = "Quiet slate right now",
  description,
  totalBeforeFilters,
  onResetFilters,
  onRetry,
  trackingVariant = "first_time",
  showPeakTimeChart = false,
}: FirstTimeEmptyStateProps) {
  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    phCapture("empty_state_shown", { variant: trackingVariant, page });
  }, [page, trackingVariant]);

  const body = description
    ? description
    : `Lines usually post closer to game time. Games update every ${LANDING_STATS.ODDS_REFRESH_DISPLAY} during live events, and most of the board fills in during evening games.`;
  const filterHint = hasFilters
    ? totalBeforeFilters && totalBeforeFilters > 0
      ? `${totalBeforeFilters.toLocaleString()} results were available before your current filters.`
      : "Try adjusting your filters for more results."
    : null;

  return (
    <EmptyState
      icon={<Clock className="size-6" aria-hidden="true" />}
      title={title}
      description={filterHint ? `${body} ${filterHint}` : body}
      action={
        onRetry
          ? { label: "Retry", onClick: onRetry }
          : hasFilters && onResetFilters
            ? { label: "Reset all filters", onClick: onResetFilters }
            : undefined
      }
      secondary={(
        <>
          {showPeakTimeChart ? <PeakTimeChart className="mt-2" /> : null}
          <ToolNavigationLink
            href="/whale-watch"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            See what's moving on Whale Watch →
          </ToolNavigationLink>
        </>
      )}
      className="mx-auto max-w-md border-0 bg-transparent shadow-none"
    />
  );
}
